import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertBanner, Button, Card, GovernmentIdentity, QrCode, Skeleton, TextInput } from '@ministry/ui/internal';
import Check from 'lucide-react/dist/esm/icons/check.js';
import Copy from 'lucide-react/dist/esm/icons/copy.js';

import { beginMfaEnrolment, confirmMfaEnrolment } from '../lib/auth.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { useT } from '../i18n/strings.js';
import { LocaleToggle } from '../components/LocaleToggle.js';

const CODE_LENGTH = 6;

/**
 * Where the token this page needs is remembered between the query string and a
 * same-tab refresh. Not a security boundary — the token is still a single redemption
 * away from becoming useless the moment it is confirmed, and the server, not this
 * storage, decides whether it is still valid.
 */
const TOKEN_STORAGE_KEY = 'ms-mfa-enrolment-token';

function readStoredToken(): string {
  try {
    return window.sessionStorage.getItem(TOKEN_STORAGE_KEY) ?? '';
  } catch {
    // Private-browsing / storage-disabled: the token still works for this one page
    // view from the in-memory state below, it just cannot survive a hard refresh.
    return '';
  }
}

/**
 * The screen that did not exist: R-033 added `POST /auth/mfa/enrol` and
 * `POST /auth/mfa/enrol/confirm` to the anonymous allowlist so `secretary` and
 * `super_admin` accounts — the only two roles MFA is mandatory for, and exactly what a
 * new ministry's first account is — could get a second factor before they could log in
 * at all. Both endpoints existed, tested and reachable by curl, with no screen calling
 * them; a newly created account had a working API and no way to reach it.
 *
 * Reached as `/enrol/mfa?token=...` — unauthenticated on purpose, the same as
 * `/reset-password`, because the whole point is that these accounts cannot obtain a
 * session of any kind until enrolment is done.
 *
 * Two calls, two different failure disciplines:
 *  - `beginMfaEnrolment` is deliberately REPEATABLE within the token's one-hour life —
 *    a refresh returns the same secret rather than a new one, so it must never be
 *    treated as single-use here (no "already redeemed" local flag).
 *  - `confirmMfaEnrolment` is what actually activates the secret. A wrong code
 *    (`AUTH_MFA_INVALID`) is a retry; an expired/invalid token (`NOT_FOUND`) is not —
 *    it needs a fresh token from an administrator, a different action entirely, so the
 *    two are never shown as the same message.
 */
export function MfaEnrolPage(): React.JSX.Element {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlToken = searchParams.get('token');

  const [token] = React.useState<string>(() => {
    if (urlToken) {
      try {
        window.sessionStorage.setItem(TOKEN_STORAGE_KEY, urlToken);
      } catch {
        /* storage unavailable — the value below still works for this page view */
      }
      return urlToken;
    }
    return readStoredToken();
  });

  React.useEffect(() => {
    if (!urlToken) return;
    // The token is a bearer credential for the platform's most privileged accounts —
    // it should not sit in the address bar, browser history, or a shared screenshot
    // any longer than the moment it takes this page to read it. `token` (captured in
    // state above, from sessionStorage) keeps working after this for a same-tab
    // refresh, which is what `beginMfaEnrolment`'s repeatability exists to support.
    const next = new URLSearchParams(searchParams);
    next.delete('token');
    setSearchParams(next, { replace: true });
    // Deliberately runs once, right after mount, off the value read into state above —
    // re-running on every `searchParams` identity change (which this very call causes)
    // would fight itself. (No `react-hooks/exhaustive-deps` rule is configured in this
    // repo's `eslint.config.js`, so this is a plain comment, not a suppressed lint error.)
  }, []);

  const enrolQuery = useQuery({
    queryKey: ['mfa-enrol', token],
    queryFn: () => beginMfaEnrolment({ token }),
    enabled: token !== '',
    retry: false,
  });

  const [code, setCode] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  const confirmMutation = useMutation({
    mutationFn: (value: string) => confirmMfaEnrolment({ token, code: value }),
  });

  const submitConfirm = (value: string) => {
    if (value.length !== CODE_LENGTH || confirmMutation.isPending) return;
    confirmMutation.mutate(value);
  };

  const handleCopySecret = (secret: string) => {
    void navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 3000);
    });
  };

  let body: React.ReactNode;

  if (token === '') {
    // No token at all: not "loading" and not a server error, so it gets its own state
    // rather than a spinner that never resolves or a generic error banner nothing
    // actually failed to produce.
    body = <AlertBanner variant="critical" title={t('mfaEnrol.noToken.title')}>{t('mfaEnrol.noToken.body')}</AlertBanner>;
  } else if (enrolQuery.isLoading) {
    body = (
      <div className="flex flex-col gap-4">
        <span role="status" aria-live="polite" className="sr-only">
          {t('mfaEnrol.loading')}
        </span>
        <Skeleton shape="block" className="h-44 w-44 self-center" />
        <Skeleton shape="list-row" count={2} />
      </div>
    );
  } else if (enrolQuery.isError) {
    const error = enrolQuery.error;
    if (error instanceof ApiError && error.code === 'NOT_FOUND') {
      body = (
        <AlertBanner variant="critical" title={t('mfaEnrol.tokenInvalid.title')}>
          {t('mfaEnrol.tokenInvalid.body')}
        </AlertBanner>
      );
    } else if (error instanceof NetworkError || (error instanceof ApiError && error.retryable)) {
      body = (
        <AlertBanner
          variant="critical"
          title={t('mfaEnrol.error.network')}
          action={
            <Button size="sm" variant="secondary" onClick={() => void enrolQuery.refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      );
    } else {
      body = <AlertBanner variant="critical" title={t('mfaEnrol.error.generic')} />;
    }
  } else if (enrolQuery.data) {
    const { secretBase32, enrolmentUri, username } = enrolQuery.data;

    if (confirmMutation.isSuccess) {
      // Never render the secret again once it is live on the account (it just became a
      // real second factor) — this branch does not read `enrolQuery.data` for it, and
      // nothing below re-enables that.
      body = (
        <div className="flex flex-col gap-4">
          <AlertBanner variant="info" title={t('mfaEnrol.confirmed.title')}>
            {t('mfaEnrol.confirmed.body')}
          </AlertBanner>
          <Button asChild>
            <Link to="/login">{t('mfaEnrol.confirmed.goToLogin')}</Link>
          </Button>
        </div>
      );
    } else {
      const confirmError = confirmMutation.error;
      let confirmErrorBanner: React.ReactNode = null;
      if (confirmError instanceof ApiError && confirmError.code === 'AUTH_MFA_INVALID') {
        confirmErrorBanner = <AlertBanner variant="critical" title={t('mfaEnrol.confirm.error.invalid')} />;
      } else if (confirmError instanceof ApiError && confirmError.code === 'NOT_FOUND') {
        confirmErrorBanner = (
          <AlertBanner variant="critical" title={t('mfaEnrol.tokenInvalid.title')}>
            {t('mfaEnrol.tokenInvalid.body')}
          </AlertBanner>
        );
      } else if (confirmError instanceof NetworkError || (confirmError instanceof ApiError && confirmError.retryable)) {
        confirmErrorBanner = (
          <AlertBanner
            variant="critical"
            title={t('mfaEnrol.error.network')}
            action={
              <Button size="sm" variant="secondary" onClick={() => submitConfirm(code)}>
                {t('common.retry')}
              </Button>
            }
          />
        );
      } else if (confirmError) {
        confirmErrorBanner = <AlertBanner variant="critical" title={t('mfaEnrol.error.generic')} />;
      }

      body = (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-muted">{t('mfaEnrol.instructions', { username })}</p>

          <QrCode value={enrolmentUri} label={t('mfaEnrol.qrLabel')} size={192} className="self-center" />

          <div className="flex flex-col gap-2">
            <p className="text-sm text-text-muted">{t('mfaEnrol.secretLabel')}</p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 select-all break-all rounded-sm bg-surface-base px-3 py-2 font-mono text-sm text-text-primary">
                {secretBase32}
              </code>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => handleCopySecret(secretBase32)}
              >
                {copied ? (
                  <Check aria-hidden="true" width={16} height={16} />
                ) : (
                  <Copy aria-hidden="true" width={16} height={16} />
                )}
                {copied ? t('mfaEnrol.secretCopied') : t('mfaEnrol.secretCopy')}
              </Button>
            </div>
          </div>

          {confirmErrorBanner}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitConfirm(code);
            }}
            className="flex flex-col gap-4"
          >
            <TextInput
              id="mfa-enrol-code"
              label={t('mfaEnrol.codeLabel')}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
              value={code}
              onChange={(event) => {
                const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
                setCode(digitsOnly);
              }}
            />
            <Button type="submit" loading={confirmMutation.isPending} loadingLabel={t('mfaEnrol.confirming')}>
              {t('mfaEnrol.confirmSubmit')}
            </Button>
          </form>
        </div>
      );
    }
  } else {
    body = null;
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface-page">
      <header className="flex items-center justify-between gap-4 px-4 py-3">
        <GovernmentIdentity />
        <LocaleToggle />
      </header>

      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4">
        <Card className="w-full max-w-sm p-6">
          <h1 className="mb-4 text-xl font-semibold text-text-heading">{t('mfaEnrol.title')}</h1>
          {body}
        </Card>
      </div>
    </div>
  );
}
